import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listProjectSnapshots, rollbackProjectSnapshot, getProjectSnapshot } from '../../api/projects';
import { saveProjectSnapshot } from '../../api/projects';
import type { SnapshotVersion } from '../../types/estimation';
import { collectSnapshotData, restoreSnapshotData } from '../../utils/snapshotData';
import { VersionDiffView } from './VersionDiffView';
import { RollbackConfirmDialog } from './RollbackConfirmDialog';
import { SaveSnapshotDialog } from './SaveSnapshotDialog';
import { toast } from '../../stores/uiStore';
import styles from './VersionHistoryPanel.module.css';

interface Props {
  projectId: string;
}

export function VersionHistoryPanel({ projectId }: Props) {
  const { t } = useTranslation('common');
  const [versions, setVersions] = useState<SnapshotVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [rollingBack, setRollingBack] = useState<number | null>(null);
  const [confirmRollback, setConfirmRollback] = useState<number | null>(null);
  const [diffPair, setDiffPair] = useState<[number, number] | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadVersions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listProjectSnapshots(projectId);
      setVersions(data);
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // Ctrl+S / Cmd+S keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        setShowSaveDialog(true);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  async function handleSave(name: string, summary: string) {
    try {
      setSaving(true);
      const { estimation_data, n2_overrides } = collectSnapshotData();
      await saveProjectSnapshot(projectId, {
        snapshot_name: name || undefined,
        estimation_data,
        n2_overrides,
        change_summary: summary || undefined,
      });
      toast.success(t('versionSaved', 'Version saved.'));
      setShowSaveDialog(false);
      await loadVersions();
    } catch (err) {
      console.error('Failed to save snapshot:', err);
      toast.error(t('failedToSaveVersion', 'Failed to save version. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleRollback(targetVersion: number, changeSummary: string) {
    try {
      setRollingBack(targetVersion);
      setConfirmRollback(null);
      await rollbackProjectSnapshot(projectId, targetVersion, changeSummary);

      // Fetch the rolled-back version data and hydrate stores
      try {
        const detail = await getProjectSnapshot(projectId, targetVersion);
        restoreSnapshotData(
          detail.estimation_data,
          detail.n2_overrides ?? null,
        );
      } catch (hydrateErr) {
        console.error('Failed to hydrate stores after rollback:', hydrateErr);
      }

      toast.success(t('rolledBackTo', { version: targetVersion }));
      await loadVersions();
    } catch (err) {
      console.error('Failed to rollback version:', err);
      toast.error(t('failedToRollback', 'Failed to rollback. Please try again.'));
    } finally {
      setRollingBack(null);
    }
  }

  function handleCompare(versionNum: number) {
    // Compare with the next version (or current)
    const currentVersion = versions.find((v) => v.is_current);
    if (currentVersion && currentVersion.version_number !== versionNum) {
      setDiffPair([versionNum, currentVersion.version_number]);
    }
  }

  if (loading) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h3 className={styles.title}>{t('versionHistory')}</h3>
          <button
            className={styles.saveBtn}
            onClick={() => setShowSaveDialog(true)}
          >
            {t('save')}
          </button>
        </div>
        <div className={styles.loading}>{t('loadingVersions', 'Loading versions...')}</div>
        {showSaveDialog && (
          <SaveSnapshotDialog
            onSave={handleSave}
            onCancel={() => setShowSaveDialog(false)}
            saving={saving}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h3 className={styles.title}>{t('versionHistory')}</h3>
        <button
          className={styles.saveBtn}
          onClick={() => setShowSaveDialog(true)}
        >
          {t('save')}
        </button>
      </div>

      {versions.length === 0 && (
        <div className={styles.empty}>{t('noVersionsSaved', 'No versions saved yet. Click Save to create the first version.')}</div>
      )}

      {/* Diff View */}
      {diffPair && (
        <div className={styles.diffSection}>
          <div className={styles.diffHeader}>
            <span>{t('comparingVersions', 'Comparing versions')}</span>
            <button
              className={styles.closeDiffBtn}
              onClick={() => setDiffPair(null)}
            >
              {t('close')}
            </button>
          </div>
          <VersionDiffView
            projectId={projectId}
            versionA={diffPair[0]}
            versionB={diffPair[1]}
          />
        </div>
      )}

      {versions.length > 0 && (
        <div className={styles.list}>
          {versions.map((v) => (
            <div
              key={v.id}
              className={`${styles.versionItem} ${v.is_current ? styles.current : ''}`}
            >
              <div className={styles.versionHeader}>
                <span className={styles.versionNum}>
                  v{v.version_number}
                  {v.snapshot_name && ` — ${v.snapshot_name}`}
                </span>
                {v.is_current && (
                  <span className={`${styles.badge} ${styles.currentBadge}`}>{t('current')}</span>
                )}
                {v.rolled_back_from_version && (
                  <span className={`${styles.badge} ${styles.rollbackBadge}`}>
                    {t('rollbackFrom', { version: v.rolled_back_from_version })}
                  </span>
                )}
              </div>
              {v.change_summary && (
                <div className={styles.changeSummary}>{v.change_summary}</div>
              )}
              <div className={styles.meta}>
                <span>{v.created_by_user_id?.slice(0, 8) ?? t('system', 'system')}</span>
                <span>{new Date(v.created_at).toLocaleString('fr-CH')}</span>
              </div>
              <div className={styles.actions}>
                {!v.is_current && (
                  <>
                    <button
                      className={styles.compareBtn}
                      onClick={() => handleCompare(v.version_number)}
                    >
                      {t('compare', 'Compare')}
                    </button>
                    <button
                      className={`${styles.actionBtn} ${styles.rollbackBtn}`}
                      onClick={() => setConfirmRollback(v.version_number)}
                      disabled={rollingBack === v.version_number}
                    >
                      {rollingBack === v.version_number ? t('rollingBack', 'Rolling back...') : t('rollbackToVersion')}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmRollback !== null && (
        <RollbackConfirmDialog
          targetVersion={confirmRollback}
          onConfirm={(summary) => handleRollback(confirmRollback, summary)}
          onCancel={() => setConfirmRollback(null)}
        />
      )}

      {showSaveDialog && (
        <SaveSnapshotDialog
          onSave={handleSave}
          onCancel={() => setShowSaveDialog(false)}
          saving={saving}
        />
      )}
    </div>
  );
}
